/**
 * Utility functions for debouncing API requests and preventing screen flicker
 */

/**
 * Creates a debounced version of a function that delays execution until after wait milliseconds
 * @param {Function} func - The function to debounce
 * @param {number} wait - The number of milliseconds to delay
 * @returns {Function} The debounced function
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Creates a throttled version of a function that limits execution to once per interval
 * @param {Function} func - The function to throttle
 * @param {number} limit - The time interval in milliseconds
 * @returns {Function} The throttled function
 */
export const throttle = (func, limit = 300) => {
  let inThrottle;
  
  return function(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Debounce map to track multiple API endpoints with separate debounce timers
 * This prevents API calls with the same signature from firing too frequently
 */
const apiDebounceMap = new Map();

/**
 * Debounces an API call to prevent screen flicker from multiple rapid calls
 * @param {string} key - Unique identifier for this API call
 * @param {Function} apiCall - The API function to execute
 * @param {number} delay - Debounce delay in milliseconds
 * @returns {Promise} - Returns a promise that resolves with the API result
 */
export const debounceApiCall = (key, apiCall, delay = 300) => {
  return new Promise((resolve, reject) => {
    // Check if we already have a pending call for this key
    if (apiDebounceMap.has(key)) {
      clearTimeout(apiDebounceMap.get(key).timerId);
    }
    
    // Create a new timer
    const timerId = setTimeout(async () => {
      try {
        // Execute the API call
        const result = await apiCall();
        resolve(result);
        // Clear from map after execution
        apiDebounceMap.delete(key);
      } catch (error) {
        reject(error);
        apiDebounceMap.delete(key);
      }
    }, delay);
    
    // Store the timer ID and promise handlers
    apiDebounceMap.set(key, { 
      timerId,
      resolve,
      reject
    });
  });
};

export default {
  debounce,
  throttle,
  debounceApiCall
};